import { ContentSearchTaskStatus } from '../../api/client'

interface SearchStatusIndicatorProps {
    status: ContentSearchTaskStatus | null
    message: string
    elapsedMs: number
    isSlowSearch: boolean
}

function formatElapsedTime(ms: number): string {
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) {
        return `${seconds}s`
    }
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
}

function getStatusMessage(status: ContentSearchTaskStatus | null, message: string): string {
    if (message) return message

    switch (status) {
        case ContentSearchTaskStatus.Pending:
            return 'Searching...'
        case ContentSearchTaskStatus.RetrievingData:
            return 'Retrieving...'
        case ContentSearchTaskStatus.Completed:
            return 'Search complete'
        case ContentSearchTaskStatus.Failed:
            return 'Search failed'
        default:
            return 'Searching...'
    }
}

function SearchStatusIndicator({
    status,
    message,
    elapsedMs,
    isSlowSearch
}: SearchStatusIndicatorProps) {
    const statusMessage = getStatusMessage(status, message)
    const elapsedTime = formatElapsedTime(elapsedMs)

    return (
        <div className="flex flex-col items-center justify-center py-12 px-4 min-h-[280px] w-full">
            {/* Spinner */}
            <div className="mb-4">
                <svg
                    className="animate-spin h-10 w-10 text-primary-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                </svg>
            </div>

            {/* Status message */}
            <p className="text-gray-700 text-lg font-medium mb-2">{statusMessage}</p>

            {/* Elapsed time - always rendered to avoid layout shift, invisible when 0s */}
            <p className={`text-gray-500 text-sm mb-4 ${Math.floor(elapsedMs / 1000) > 0 ? '' : 'invisible'}`}>
                {elapsedTime}
            </p>

            {/* Slow search warning */}
            {isSlowSearch && (
                <div className="max-w-md bg-amber-50 border border-amber-200 rounded-lg p-4 animate-fadeIn">
                    <div className="flex items-start">
                        <svg
                            className="h-5 w-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <div>
                            <p className="text-amber-800 text-sm font-medium">
                                Search is taking longer than expected
                            </p>
                            <p className="text-amber-700 text-sm mt-1">
                                Consider reducing the date range or selecting fewer profiles for
                                faster results.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default SearchStatusIndicator
